/*
 * tp-byob-core.js — shared state for all three Build Your Own Bundle treatments.
 *
 * Owns one job: given the cart, work out how far the customer is along the
 * percentage ladder. Both surfaces (cart drawer progress, builder page) read
 * from this and never recompute the maths themselves.
 *
 * THE LADDER is a tier table in theme settings ("3:20,5:25,..." = at this many
 * eligible fonts, this percent off the selection). Rates are set deliberately
 * below the pre-made bundles so building your own never undercuts buying a
 * bundle — see snippets/tp-byob-readme.liquid for the comparison table.
 *
 * The ladder is expressed in admin as one automatic discount per tier, each
 * scoped to the unlisted "byob" product so a normally-assembled cart can never
 * trigger it. Several qualify at once on a large cart; Shopify picks the
 * best-value one, which is correct here because the percentages only ever rise
 * with quantity. THESE ARE TWO COPIES OF THE SAME NUMBERS — change the ladder
 * here and the discounts in admin together, or the page promises a figure
 * checkout will not honour.
 *
 * This module works from PRE-DISCOUNT prices (original_price); reading
 * final_price would apply any live discount twice over and zero the savings.
 */
window.TPByob = (function () {
  'use strict';

  var data = null;
  var eligibleIds = new Set();
  var fontsByHandle = new Map();
  var subscribers = [];
  var state = null;
  var cart = null;
  var announced = { pct: 0, threshold: null };

  function parseData() {
    var el = document.getElementById('tp-byob-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[byob] could not parse data island:', e);
      return null;
    }
  }

  /* Bundle metafields store "font-taxi-driver"; product handles are
     "taxi-driver". One product ("jukebox_johnny") also uses an underscore
     where the metafield uses a hyphen. Fold both so matching works. */
  function normalizeHandle(handle) {
    return String(handle || '')
      .trim()
      .toLowerCase()
      .replace(/^font-/, '')
      .replace(/_/g, '-');
  }

  function formatMoney(cents) {
    var format = (data && data.config && data.config.moneyFormat) || '${{amount}}';
    var noDecimals = /no_decimals/.test(format);
    var amount = noDecimals
      ? Math.round(cents / 100).toString()
      : (cents / 100).toFixed(2).replace(/\.00$/, '');
    return format.replace(/\{\{\s*[a-z_]+\s*\}\}/, amount);
  }

  /* Tier table, parsed from the "3:10,6:20,..." setting — each pair is
     "at this many fonts, this percent off". Sorted ascending so the last
     matching tier wins. */
  function tiers(override) {
    var raw = override || (data && data.config && data.config.tiers) || '';
    return String(raw)
      .split(',')
      .map(function (pair) {
        var bits = pair.split(':');
        return { at: parseInt(bits[0], 10), pct: parseFloat(bits[1]) };
      })
      .filter(function (t) { return t.at > 0 && t.pct > 0; })
      .sort(function (a, b) { return a.at - b.at; });
  }

  /* The ladder maths, over a bare list of unit prices. Shared by the cart-based
     treatments and the builder page, which runs it against an uncommitted
     selection rather than the cart.

     Percentage off the whole selection, stepping up as the count grows. The
     rates are deliberately gentler than the pre-made bundles so building your
     own never undercuts buying the bundle — see the README for the numbers. */
  function ladder(prices, tiersOverride, nouns) {
    var count = prices.length;
    var subtotal = prices.reduce(function (sum, p) { return sum + p; }, 0);

    /* tiersOverride lets a builder page run its own ladder — the illustration
       store's rates are independent of the font store's. */
    var table = tiers(tiersOverride);
    var pct = 0;
    var reached = 0;
    var next = null;

    table.forEach(function (t) {
      if (count >= t.at) { pct = t.pct; reached++; }
      else if (!next) { next = t; }
    });

    var savings = Math.round(subtotal * pct / 100);
    var payable = subtotal - savings;

    var s = {
      eligibleCount: count,
      subtotal: subtotal,
      payable: payable,
      savings: savings,
      discountPercent: pct,
      effectivePerFont: count ? Math.round(payable / count) : 0,
      nextTier: next,
      countToNextTier: next ? next.at - count : 0,
      tiersReached: reached,
      tiersTotal: table.length,
      /* The illustration store isn't selling fonts — each builder page passes
         its own noun so the shared copy reads correctly on both. */
      nouns: nouns || { one: 'font', many: 'fonts' },
      format: formatMoney
    };
    s.message = message(s);
    return s;
  }

  function computeState(cartJson) {
    /* Expand quantities into individual units — a qty of 2 is two fonts on
       the ladder, and each unit can be discounted independently. */
    var units = [];
    (cartJson && cartJson.items ? cartJson.items : []).forEach(function (item) {
      if (!eligibleIds.has(Number(item.product_id))) return;
      /* MUST be original_price, not final_price. Once the real automatic
         discount is active Shopify has already zeroed the free line, so
         final_price would make this module compute the discount on top of
         itself — savings collapse to $0 and the progress copy stops making
         sense. Working from pre-discount prices means the figures shown match
         Shopify's own total instead of shadowing it. */
      var unitPrice = typeof item.original_price === 'number' ? item.original_price : item.price;
      for (var i = 0; i < item.quantity; i++) {
        units.push({ price: unitPrice, handle: normalizeHandle(item.handle), title: item.product_title });
      }
    });

    var s = ladder(units.map(function (u) { return u.price; }));
    s.units = units;
    s.handles = units.map(function (u) { return u.handle; });
    return s;
  }

  /* Message copy is shared so the drawer and the builder page never drift
     apart. Always frames the next step, since that is what moves the count. */
  function message(s) {
    if (s.eligibleCount === 0) return '';

    var n = s.countToNextTier;
    var nouns = s.nouns || { one: 'font', many: 'fonts' };
    var word = n === 1 ? nouns.one : nouns.many;

    if (s.discountPercent === 0) {
      if (!s.nextTier) return '';
      return 'Add ' + n + ' more ' + word + ' for ' + s.nextTier.pct + '% off';
    }

    if (!s.nextTier) {
      return s.discountPercent + '% off — the best rate on the ladder.';
    }

    return s.discountPercent + '% off. ' + n + ' more ' + word + ' for ' + s.nextTier.pct + '%.';
  }

  function track(event, detail) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: event }, detail || {}));
    document.dispatchEvent(new CustomEvent('byob:' + event, { detail: detail || {} }));
  }

  /* Fire milestone events once per crossing, not on every recompute. */
  function announce(s) {
    if (s.discountPercent > announced.pct) {
      track('byob_tier_unlocked', {
        discountPercent: s.discountPercent,
        eligibleCount: s.eligibleCount,
        savings: s.savings
      });
    }
    announced.pct = s.discountPercent;

    /* One font away from the next tier — the moment worth nudging. */
    var atThreshold = s.eligibleCount > 0 && s.countToNextTier === 1;
    var key = atThreshold ? s.eligibleCount : null;
    if (atThreshold && announced.threshold !== key) {
      track('byob_threshold_reached', {
        eligibleCount: s.eligibleCount,
        nextPercent: s.nextTier ? s.nextTier.pct : null
      });
    }
    announced.threshold = key;
  }

  function setCart(cartJson) {
    cart = cartJson;
    state = computeState(cartJson);
    state.message = message(state);
    announce(state);
    subscribers.forEach(function (fn) {
      try { fn(state); } catch (e) { console.warn('[byob] subscriber failed:', e); }
    });
  }

  function refresh() {
    return fetch(window.Shopify && Shopify.routes ? Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.json(); })
      .then(function (json) { setCart(json); return state; })
      .catch(function (e) { console.warn('[byob] cart fetch failed:', e); return state; });
  }

  function addToCart(items) {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sections } })
    );

    return fetch((window.FoxTheme && FoxTheme.routes ? FoxTheme.routes.cart_add_url : '/cart/add'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: items, sections: sections, sections_url: window.location.pathname })
    })
      .then(function (r) { return r.json(); })
      .then(function (parsed) {
        if (parsed.status) {
          console.warn('[byob] add failed:', parsed.description || parsed.message);
          return null;
        }
        return refresh().then(function () {
          /* Hand the theme its own cart payload so the drawer, bubble and
             totals all update through the theme's normal path. */
          if (window.FoxTheme && FoxTheme.pubsub && cart) {
            var payload = Object.assign({}, cart, { sections: parsed.sections });
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: payload });
          }
          return parsed;
        });
      });
  }

  function subscribe(fn) {
    subscribers.push(fn);
    if (state) fn(state);
    return function unsubscribe() {
      subscribers = subscribers.filter(function (s) { return s !== fn; });
    };
  }

  /* The theme replaces cart-drawer markup wholesale via innerHTML on update,
     so treatments are built as custom elements — the browser re-runs
     connectedCallback on the new nodes. Nothing here polls or rebinds on DOM
     mutation; state changes arrive on the theme's own pubsub channel. */
  function bindCartEvents() {
    var attempts = 0;
    (function waitForTheme() {
      if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, function (payload) {
          if (payload && payload.cart && payload.cart.items) setCart(payload.cart);
          else refresh();
        });
        return;
      }
      if (attempts++ < 20) return setTimeout(waitForTheme, 100);
      /* Fallback only if the theme bundle never appears. */
      document.addEventListener('product-ajax:added', function () { refresh(); });
    })();
  }

  function indexFonts(list) {
    list.forEach(function (f) {
      if (!f || !f.id) return;
      eligibleIds.add(Number(f.id));
      var key = normalizeHandle(f.handle);
      /* Liquid entries win — they carry the font file that the JSON endpoint
         can't expose (metafields aren't part of products.json). */
      if (!fontsByHandle.has(key)) fontsByHandle.set(key, f);
    });
  }

  /* Safety net for the Liquid 50-item cap. Only runs when the rendered list
     doesn't cover the collection, so in the normal case this costs nothing. */
  function hydrateFromEndpoint() {
    return fetch(data.singlesUrl, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (json) {
        var extra = (json.products || []).map(function (p) {
          var available = (p.variants || []).filter(function (v) { return v.available; });
          var pool = available.length ? available : p.variants || [];
          var cheapest = pool.reduce(function (best, v) {
            return !best || parseFloat(v.price) < parseFloat(best.price) ? v : best;
          }, null);
          return {
            id: p.id,
            handle: p.handle,
            title: p.title,
            url: '/products/' + p.handle,
            variantId: cheapest ? cheapest.id : null,
            price: cheapest ? Math.round(parseFloat(cheapest.price) * 100) : 0,
            fontFile: null
          };
        });

        var known = new Set(data.fonts.map(function (f) { return Number(f.id); }));
        var added = extra.filter(function (f) { return !known.has(Number(f.id)); });

        indexFonts(added);
        data.fonts = data.fonts.concat(added);

        console.warn(
          '[byob] Liquid rendered ' + (data.fonts.length - added.length) + ' of ' +
          data.singlesCount + ' singles; recovered ' + added.length +
          ' from the collection endpoint. Those fonts have no webfont file and will ' +
          'render in the UI typeface on the builder page until a bundle references them.'
        );

        if (cart) setCart(cart);
      })
      .catch(function (e) { console.warn('[byob] endpoint hydration failed:', e); });
  }

  function init() {
    data = parseData();
    if (!data) return;

    indexFonts(data.fonts);

    if (data.singlesCount && data.fonts.length < data.singlesCount) {
      hydrateFromEndpoint();
    }

    bindCartEvents();
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  return {
    subscribe: subscribe,
    refresh: refresh,
    addToCart: addToCart,
    ladder: ladder,
    getState: function () { return state; },
    getData: function () { return data; },
    getFont: function (handle) { return fontsByHandle.get(normalizeHandle(handle)); },
    normalizeHandle: normalizeHandle,
    formatMoney: formatMoney,
    track: track
  };
})();
